import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Stack, Paper, Group, TextInput, Select, Button, Badge, Anchor, CloseButton,
  Center, Loader, Text, Alert,
} from '@mantine/core'
import { DateInput } from '@mantine/dates'
import {
  IconDownload, IconInfoCircle, IconSearch, IconX,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { notifications } from '@mantine/notifications'
import { PageTitle, TabStrip } from '@lighthouse/design'
import { AuditDetailModal } from '../../components/audit/AuditDetailModal'
import { AuditTrailTable } from '../../components/audit/AuditTrailTable'
import { RestrictedState } from '../../components/access/RestrictedState'
import {
  readAuditLink, resolveCategory, writeAuditLink,
} from '../../components/audit/auditFormat'
import type { AuditLink } from '../../components/audit/auditFormat'
import { useLighthouseAdmin } from '../../hooks/useLighthouseAdmin'
import { getAuditLog, exportAuditLog, getAuditCategories } from '../../services/auditLogApi'
import { errorKind, errorSummary } from '../../utils/format'
import type { AuditLogQuery } from '../../types/auditLog'

const PAGE_SIZE = 50

/**
 * The CRUD vocabulary the EF interceptor writes.
 *
 * Only half the trail's operations, and it was the whole of this picker until the
 * category arrived. Cartographer's governance auditor writes to the same table
 * through a different code path with its own vocabulary — `Grant`,
 * `WorkspaceMemberRemoved`, `PurgeDelete` — so a `Grant` row has always been in
 * this log and has never been selectable here.
 *
 * These three stay hardcoded because they are this SPA's own writer's vocabulary,
 * fixed by the interceptor's three branches. The GOVERNANCE operations are not
 * listed anywhere in this app: they come from the server with the category, for
 * the reason on `AuditCategory`.
 */
const INTERCEPTOR_OPERATIONS = ['Create', 'Update', 'Delete']

interface FilterState {
  entityType: string
  userId: string
  operation: string | null
  /**
   * `YYYY-MM-DD`, because that is what Mantine 8's `DateInput` emits.
   *
   * These were `Date` with the onChange argument cast to match, which type-checks
   * and then throws `value.toISOString is not a function` on the first filtered
   * search — `onChange` is declared `(value: DateStringValue | null) => void`, a
   * string. The cast told the compiler not to look. Fixed 2026-09-24.
   */
  from: string | null
  to: string | null
}

const EMPTY_FILTERS: FilterState = {
  entityType: '',
  userId: '',
  operation: null,
  from: null,
  to: null,
}

function toQuery(
  filters: FilterState, page: number, category: string | null, entityId: string | null,
): AuditLogQuery {
  return {
    entityType: filters.entityType.trim() || undefined,
    entityId: entityId ?? undefined,
    userId: filters.userId.trim() || undefined,
    operation: filters.operation || undefined,
    category: category ?? undefined,
    from: dayBoundary(filters.from, '00:00:00.000'),
    // Include the whole "to" day by pushing to end-of-day.
    to: dayBoundary(filters.to, '23:59:59.999'),
    skip: (page - 1) * PAGE_SIZE,
    top: PAGE_SIZE,
  }
}

/**
 * A `YYYY-MM-DD` from the picker, as the absolute instant the API filters on.
 *
 * The time is appended without a trailing `Z` on purpose: `new Date('2026-08-13')`
 * parses as UTC midnight and `new Date('2026-08-13T00:00:00.000')` as local, and
 * the picker means the day the reader is looking at.
 */
function dayBoundary(day: string | null, time: string): string | undefined {
  if (!day) return undefined
  const parsed = new Date(`${day}T${time}`)
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString()
}

/**
 * The audit trail. AuditLogController is LighthouseAdmin at class level, so this page is too.
 *
 * The category, the page and the entity id live in the URL, so a view of the trail is a link another
 * surface can send: the Governance Queue sends `/audit?category=permissions`, and the access drawer
 * sends `/audit?entityId=<report or workspace id>` — a revoked grant is a hard delete, so this trail is
 * the only record the access existed. The typed filters stay in component state; nobody links a
 * half-typed user id.
 */
export function AuditLogPage() {
  // The server's answer, not a token claim — see useLighthouseAdmin. On `denied` nothing below fetches;
  // on `unknown` the page runs normally and a 403 from its own queries lands in the restricted state.
  const gate = useLighthouseAdmin()
  const denied = gate.state === 'denied'
  // Not answered yet (a cold load): fetch nothing until it is, or a denied caller's requests race its "no".
  const waiting = gate.state === 'pending'

  const [searchParams, setSearchParams] = useSearchParams()
  const link = readAuditLink(searchParams)

  // Draft filters edited in the form; applied filters drive the query.
  const [draft, setDraft] = useState<FilterState>(EMPTY_FILTERS)
  const [applied, setApplied] = useState<FilterState>(EMPTY_FILTERS)
  const [detailId, setDetailId] = useState<string | null>(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [exporting, setExporting] = useState(false)

  // Fetched once and cached hard: the categories are a deployed constant, not
  // state. What they must NOT be is a list in this file -- see AuditCategory.
  const categoriesQuery = useQuery({
    queryKey: ['audit-categories'],
    queryFn: getAuditCategories,
    staleTime: Infinity,
    enabled: !denied && !waiting,
  })
  const categories = categoriesQuery.data ?? []
  // A failed REFETCH keeps the list it had, so only a list that never arrived is unavailable.
  const categoriesUnavailable = categoriesQuery.isError && categoriesQuery.data === undefined

  // null is "All activity". The key applied is always one the server published: a link's key is
  // checked against the served list, never trusted as typed.
  const resolution = resolveCategory(link.category, categoriesQuery.data, categoriesUnavailable)
  const category = resolution.applied
  const activeCategory = categories.find((c) => c.key === category) ?? null

  const query = toQuery(applied, link.page, category, link.entityId)

  const { data, isLoading, error } = useQuery({
    queryKey: ['audit-log', query],
    queryFn: () => getAuditLog(query),
    // Held while a linked category is still being checked: running it would show the whole trail
    // under a link that promised a filtered one, for as long as the list takes to arrive.
    enabled: !denied && !waiting && resolution.state !== 'pending',
  })

  // Keep the URL saying what the page shows: an unknown category key is dropped, as are page 1 and a
  // page that is not a number. A category that could not be CHECKED stays — the link may be good.
  const canonical = writeAuditLink(searchParams, { ...link, category: resolution.inUrl }).toString()
  useEffect(() => {
    if (canonical !== searchParams.toString()) {
      setSearchParams(new URLSearchParams(canonical), { replace: true })
    }
  }, [canonical, searchParams, setSearchParams])

  const writeLink = (next: Partial<AuditLink>) => {
    setSearchParams(
      writeAuditLink(searchParams, { ...link, category: resolution.inUrl, ...next }),
      { replace: true },
    )
  }

  const setPage = (page: number) => writeLink({ page })

  const applyFilters = () => {
    setApplied(draft)
    writeLink({ page: 1 })
  }

  const clearFilters = () => {
    setDraft(EMPTY_FILTERS)
    setApplied(EMPTY_FILTERS)
    // The entity is a filter too, and "Clear" that left it in place would be the page disagreeing
    // with its own button. The category is a view, not a filter, and stays.
    writeLink({ page: 1, entityId: null })
  }

  /**
   * Switching view clears the operation, because it does not survive the move.
   *
   * The operations on offer differ per tab -- Create/Update/Delete on the full
   * trail, the governance set under a category -- so carrying one across would
   * leave a filter applied that the visible picker cannot show and the new tab
   * cannot match. The result is an empty table with no visible cause, which on an
   * audit surface reads as "nothing happened".
   */
  const switchCategory = (next: string | null) => {
    writeLink({ category: next, page: 1 })
    setDraft({ ...draft, operation: null })
    setApplied({ ...applied, operation: null })
  }

  const openDetail = (id: string) => {
    setDetailId(id)
    setModalOpen(true)
  }

  const handleExport = async () => {
    setExporting(true)
    try {
      // The visible query itself, so the file is the view on screen — category, entity and every
      // filter — by construction rather than by a second call that could drift. The export drops
      // the paging itself.
      const { blob, filename } = await exportAuditLog(query)
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = filename
      anchor.click()
      window.URL.revokeObjectURL(url)
      notifications.show({ title: 'Export complete', message: `Downloaded ${filename}`, color: 'green' })
    } catch (err) {
      notifications.show({
        title: 'Export failed',
        // First line only, as in the results panel: a text error body is carried whole in the message.
        message: errorSummary(err) ?? 'Failed to export audit log',
        color: 'red',
      })
    } finally {
      setExporting(false)
    }
  }

  const page = link.page

  const title = (
    <PageTitle subtitle="Every change made through the API, who made it, and what it replaced">
      Audit Log
    </PageTitle>
  )

  // Both endpoints sit under the controller's one class-level policy, so a 403 from either is the
  // caller's role, not a fault — and a refused request is never rendered as an empty trail.
  if (denied || errorKind(error) === 'forbidden' || errorKind(categoriesQuery.error) === 'forbidden') {
    return (
      <Stack gap="lg">
        {title}
        <RestrictedState surface="Audit Log" reason={gate.state === 'denied' ? gate.reason : null} />
      </Stack>
    )
  }

  // Asked nothing yet, so it shows nothing yet — never "No audit entries" for a question not put.
  if (waiting) {
    return (
      <Stack gap="lg">
        {title}
        <Center py="xl" aria-busy="true" data-state="gate-pending"><Loader /></Center>
      </Stack>
    )
  }

  return (
    <Stack gap="lg">
      {title}

      {/*
        Two views of one trail, not two pages. "Recent permission changes" is the
        same rows filtered to the operations that change who can reach something —
        so it belongs here, where the detail modal, the export and the date filters
        already work, rather than as a second surface that would drift from this
        one.
      */}
      <TabStrip
        tabs={[
          { value: 'all', label: 'All activity' },
          ...categories.map((c) => ({ value: c.key, label: c.label })),
        ]}
        // While a linked key is being checked no tab is selected: "All activity" would claim a view
        // the page has not decided on yet.
        value={resolution.state === 'pending' ? (resolution.inUrl ?? 'all') : (category ?? 'all')}
        onChange={(value) => switchCategory(value === 'all' ? null : value)}
      />

      {/*
        The list failed, so the tabs above are only "All activity". Said out loud: a
        page offering no categories and saying nothing reads as a trail that has none.
      */}
      {categoriesUnavailable && (
        <Group gap={6} wrap="nowrap" align="flex-start" role="status">
          <IconInfoCircle size={14} style={{ flexShrink: 0, marginTop: 3, color: 'var(--color-text-secondary)' }} />
          <Text size="xs" c="dimmed">
            Categories unavailable — showing all activity.
            {resolution.state === 'unavailable' && (
              <> The link asked for “{resolution.inUrl}”, which cannot be checked until they load.</>
            )}
            {errorSummary(categoriesQuery.error) && <> ({errorSummary(categoriesQuery.error)})</>}
            {' '}
            <Anchor component="button" type="button" size="xs" onClick={() => void categoriesQuery.refetch()}>
              Retry
            </Anchor>
          </Text>
        </Group>
      )}

      {activeCategory && (
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light" title={activeCategory.label}>
          <Text size="sm">{activeCategory.description}</Text>
        </Alert>
      )}

      <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        {/*
          The entity arrives by link, not by form: it is a pivot from another
          surface, and a GUID is not something anyone types. It composes with the
          category and every filter below, and the export carries it.
        */}
        {link.entityId && (
          <Group gap="xs" mb="sm">
            <Badge
              size="lg"
              variant="light"
              color="blue"
              tt="none"
              rightSection={
                <CloseButton
                  size="xs"
                  aria-label="Show entries for every entity"
                  onClick={() => writeLink({ entityId: null, page: 1 })}
                />
              }
            >
              Entries for {link.entityId}
            </Badge>
          </Group>
        )}
        <Group align="flex-end" gap="sm">
          <TextInput
            label="Entity type"
            placeholder="e.g. DimAccount"
            value={draft.entityType}
            onChange={(e) => setDraft({ ...draft, entityType: e.currentTarget.value })}
          />
          <TextInput
            label="User"
            placeholder="User id"
            value={draft.userId}
            onChange={(e) => setDraft({ ...draft, userId: e.currentTarget.value })}
          />
          {/*
            The options come from the active category when there is one, so this
            app never holds its own list of what a governance operation is. On the
            full trail it falls back to the interceptor's three, which are this
            SPA's own writer's vocabulary rather than a policy decision.
          */}
          <Select
            label="Operation"
            placeholder="Any"
            clearable
            data={activeCategory ? activeCategory.operations : INTERCEPTOR_OPERATIONS}
            value={draft.operation}
            onChange={(value) => setDraft({ ...draft, operation: value })}
            w={220}
          />
          <DateInput
            label="From"
            placeholder="Start date"
            clearable
            value={draft.from}
            onChange={(value) => setDraft({ ...draft, from: value })}
            w={150}
          />
          <DateInput
            label="To"
            placeholder="End date"
            clearable
            value={draft.to}
            onChange={(value) => setDraft({ ...draft, to: value })}
            w={150}
          />
          <Button leftSection={<IconSearch size={16} />} onClick={applyFilters}>
            Search
          </Button>
          <Button variant="subtle" leftSection={<IconX size={16} />} onClick={clearFilters}>
            Clear
          </Button>
          <Button
            variant="light"
            leftSection={<IconDownload size={16} />}
            onClick={handleExport}
            loading={exporting}
            // Not while a linked category is being checked: the file would be the whole trail.
            disabled={resolution.state === 'pending'}
            ml="auto"
          >
            Export
          </Button>
        </Group>
      </Paper>

      <Paper p="md" withBorder style={{ backgroundColor: 'var(--color-bg-surface)' }}>
        {/*
          Loading, failed, rows, empty and past-the-end, partitioned once in
          AuditTrailTable (shared with Report Detail's History tab). A 403 never
          reaches it — it is the restricted state above.
        */}
        <AuditTrailTable
          data={data}
          isLoading={isLoading}
          error={error}
          page={page}
          onPageChange={setPage}
          onOpenDetail={openDetail}
          badRequestHint={
            /*
              A malformed ?entityId= is refused by model binding with only "One or
              more validation errors occurred." Everything else this page sends it
              built itself, so the link's id is the likely cause.
            */
            link.entityId ? (
              <Text size="sm" mt={4}>
                The server refused the request. An entity id must be a GUID; clear “{link.entityId}” above
                to see every entity.
              </Text>
            ) : undefined
          }
        />
      </Paper>

      <AuditDetailModal logId={detailId} opened={modalOpen} onClose={() => setModalOpen(false)} />
    </Stack>
  )
}
